import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isEditor } from "@/lib/roles";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import SecureDocViewer from "@/components/SecureDocViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MatterCreateDialog } from "@/components/matters/MatterCreateDialog";
import { MatterList } from "@/components/matters/MatterList";
import { MatterDetail } from "@/components/matters/MatterDetail";
import { PlaybookTab } from "@/components/matters/PlaybookTab";
import { FolderKanban, Search, ListChecks, UserCheck } from "lucide-react";
import { toast } from "sonner";

export default function MatterLifecycle() {
  const { user } = useAuth();
  const editor = isEditor(user?.role);
  const [stages, setStages] = useState([]);
  const [matters, setMatters] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [groups, setGroups] = useState([]);
  const [guidance, setGuidance] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [mine, setMine] = useState(false);

  const loadMatters = useCallback(() => {
    const params = {};
    if (status !== "all") params.status = status;
    if (q) params.q = q;
    if (mine && user?.email) params.assignee = user.email;
    return api.get("/matters", { params }).then((r) => {
      setMatters(r.data);
      setActiveId((cur) => (cur && !r.data.some((m) => m.id === cur) ? null : cur));
      setActive((cur) => (cur && !r.data.some((m) => m.id === cur.id) ? null : cur));
    }).catch((e) => console.error("matters load", e));
  }, [status, q, mine, user]);

  useEffect(() => {
    api.get("/matter-template").then((r) => setStages(r.data.stages || [])).catch((e) => console.error("matter template", e));
    api.get("/practice-groups").then((r) => setGroups(r.data)).catch((e) => console.error("practice groups", e));
    api.get("/articles-filter", { params: { section: "matter" } }).then((r) => setGuidance(r.data)).catch((e) => console.error("guidance", e));
  }, []);
  useEffect(() => { loadMatters(); }, [loadMatters]);

  const selectMatter = useCallback(async (id) => {
    setActiveId(id);
    try { const { data } = await api.get(`/matters/${id}`); setActive(data); }
    catch (e) { console.error("matter detail", e); setActive(null); }
  }, []);
  useEffect(() => {
    if (!activeId && matters.length > 0) selectMatter(matters[0].id);
  }, [matters, activeId, selectMatter]);

  const applyUpdate = (data) => { setActive(data); setMatters((prev) => prev.map((m) => (m.id === data.id ? data : m))); };
  const patchChecklist = async (payload, optimistic, errMsg) => {
    if (!active) return;
    setActive((prev) => ({ ...prev, checklist: prev.checklist.map(optimistic) }));
    try { applyUpdate((await api.patch(`/matters/${active.id}/checklist`, payload)).data); }
    catch (e) { console.error(errMsg, e); toast.error(errMsg); selectMatter(active.id); }
  };
  const putMatter = async (payload, okMsg, errMsg) => {
    if (!active) return;
    try { applyUpdate((await api.put(`/matters/${active.id}`, payload)).data); if (okMsg) toast.success(okMsg); }
    catch (e) { console.error(errMsg, e); toast.error(errMsg); selectMatter(active.id); }
  };

  const create = async (form) => {
    try { const { data } = await api.post("/matters", form); toast.success("Matter created"); await loadMatters(); selectMatter(data.id); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create matter"); return false; }
  };
  const actions = {
    remove: async (id) => {
      try { await api.delete(`/matters/${id}`); toast.success("Matter deleted"); setActive(null); setActiveId(null); loadMatters(); }
      catch (e) { console.error("delete matter", e); toast.error("Could not delete"); }
    },
    toggleItem: (stageKey, item, done) => patchChecklist({ stage: stageKey, item, done }, (c) => (c.stage === stageKey && c.item === item ? { ...c, done } : c), "Could not update"),
    setDue: (stageKey, item, due) => patchChecklist({ stage: stageKey, item, due }, (c) => (c.stage === stageKey && c.item === item ? { ...c, due: due || null } : c), "Could not set due date"),
    setStage: (stageKey) => putMatter({ stage: stageKey }, null, "Could not update stage"),
    setStatus: (s) => putMatter({ status: s }, s === "closed" ? "Matter closed" : "Matter reopened", "Could not update status"),
    assign: (name, email) => {
      setActive((prev) => ({ ...prev, assigned_to: name, assigned_to_email: email }));
      return putMatter({ assigned_to: name, assigned_to_email: email }, name ? `Assigned to ${name}` : "Unassigned", "Could not update assignee");
    },
  };
  const stageName = (key) => stages.find((s) => s.key === key)?.name || key;

  return (
    <div data-testid="matter-page">
      <PageHeader overline="Operations" title="Matter Lifecycle" subtitle="Track each matter through intake, engagement, litigation and closing — with per-stage checklists." />

      <Tabs defaultValue="matters">
        <TabsList className="mb-6">
          <TabsTrigger value="matters" data-testid="tab-matters"><FolderKanban className="h-4 w-4 mr-1.5" /> Matters</TabsTrigger>
          <TabsTrigger value="playbook" data-testid="tab-playbook"><ListChecks className="h-4 w-4 mr-1.5" /> Playbook &amp; Template</TabsTrigger>
        </TabsList>

        <TabsContent value="matters">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="matter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, matter #, title…" className="pl-9" />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40" data-testid="matter-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Button variant={mine ? "default" : "outline"} data-testid="my-matters-toggle" onClick={() => setMine((v) => !v)}>
              <UserCheck className="h-4 w-4 mr-1.5" /> My Matters
            </Button>
            {editor && <MatterCreateDialog groups={groups} stages={stages} onCreate={create} />}
          </div>

          {matters.length === 0 ? (
            <EmptyState icon={FolderKanban} title="No matters yet" hint={editor ? "Open your first matter with the New Matter button." : "Matters will appear here as they are opened."} />
          ) : (
            <div className="grid lg:grid-cols-3 gap-6">
              <MatterList matters={matters} activeId={activeId} onSelect={selectMatter} stageName={stageName} />
              <div className="lg:col-span-2">
                {active
                  ? <MatterDetail active={active} stages={stages} editor={editor} actions={actions} />
                  : <EmptyState icon={FolderKanban} title="Select a matter" hint="Choose a matter from the list to view its stage tracker." />}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="playbook">
          <PlaybookTab stages={stages} guidance={guidance} onOpenGuidance={setViewer} />
        </TabsContent>
      </Tabs>

      <SecureDocViewer doc={viewer} open={!!viewer} onOpenChange={(o) => !o && setViewer(null)} viewerName={user?.name} />
    </div>
  );
}
